import { type AxiosResponse, AxiosError, type AxiosInstance } from 'axios';
import type { ApiErrorResponse } from '../../types/api/common';
import type { AuthTokens } from '../../types/api/auth';

/**
 * Token Refresh Coordinator
 * 
 * Implements Singleton pattern to coordinate token refresh operations across the application.
 * Ensures only one token refresh happens at a time and properly queues concurrent requests.
 * 
 * Design Patterns:
 * - Singleton: Single instance manages all refresh operations
 * - Promise Queue: Handles concurrent request queuing
 * - Observer: Dispatches events for token state changes
 */
class TokenRefreshCoordinator {
  private static instance: TokenRefreshCoordinator;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<AuthTokens | null> | null = null;
  private failedRequestsQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];
  private axiosInstance: AxiosInstance | null = null;

  private constructor() {}

  public static getInstance(): TokenRefreshCoordinator {
    if (!TokenRefreshCoordinator.instance) {
      TokenRefreshCoordinator.instance = new TokenRefreshCoordinator();
    }
    return TokenRefreshCoordinator.instance;
  }

  public setAxiosInstance(instance: AxiosInstance): void {
    this.axiosInstance = instance;
  }

  /**
   * Check if token is currently being refreshed
   */
  public getIsRefreshing(): boolean {
    return this.isRefreshing;
  }

  /**
   * Add failed request to queue
   */
  public enqueueRequest(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.failedRequestsQueue.push({ resolve, reject });
    });
  }

  /**
   * Process all queued requests
   */
  private processQueue(error: any = null): void {
    this.failedRequestsQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });

    this.failedRequestsQueue = [];
  }

  /**
   * Check if access token is expired or about to expire
   */
  public isTokenExpired(token: string, bufferMinutes: number = 5): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid token format');
        return true;
      }

      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) {
        console.warn('Token missing expiration');
        return true;
      }

      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const buffer = bufferMinutes * 60 * 1000; // Buffer in milliseconds

      return now >= (expiry - buffer);
    } catch (error) {
      console.error('Failed to check token expiry:', error);
      return true;
    }
  }

  /**
   * Get stored tokens from storage
   */
  private getStoredTokens(): { tokens: AuthTokens; storage: Storage } | null {
    try {
      // Try sessionStorage first, then localStorage
      let tokensString = sessionStorage.getItem('tokens');
      let storage: Storage = sessionStorage;

      if (!tokensString) {
        tokensString = localStorage.getItem('tokens');
        storage = localStorage;
      }

      if (!tokensString) {
        return null;
      }

      const tokens = JSON.parse(tokensString) as AuthTokens;
      return { tokens, storage };
    } catch (error) {
      console.error('Failed to retrieve stored tokens:', error);
      return null;
    }
  }

  /**
   * Refresh authentication token with proper error handling and retry logic
   * 
   * Strategy Pattern: Implements token refresh strategy
   * Template Method: Defines refresh flow with customizable steps
   */
  public async refreshToken(): Promise<AuthTokens | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const newTokens = await this.refreshPromise;
      
      if (newTokens) {
        this.processQueue(null);
        this.dispatchTokenRefreshEvent('success', newTokens);
      } else {
        this.processQueue(new Error('Token refresh failed'));
        this.dispatchTokenRefreshEvent('failure', null);
      }

      return newTokens;
    } catch (error) {
      this.processQueue(error);
      this.dispatchTokenRefreshEvent('failure', null);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Perform actual token refresh operation
   */
  private async performTokenRefresh(): Promise<AuthTokens | null> {
    try {
      const storedData = this.getStoredTokens();

      if (!storedData) {
        console.warn('No stored tokens found for refresh');
        return null;
      }

      const { tokens, storage } = storedData;
      const refreshToken = tokens.refreshToken;

      if (!refreshToken) {
        console.warn('No refresh token available');
        return null;
      }

      console.info('🔄 Initiating token refresh...');

      // Call refresh token endpoint
      const response = await this.axiosInstance?.post('/auth/refresh', {
        refreshToken: refreshToken,
      });

      if (!response || !response.data) {
        console.error('Invalid refresh token response');
        return null;
      }

      // Extract new tokens from response (handle multiple response structures)
      const newTokens = this.extractTokensFromResponse(response.data);

      if (!newTokens) {
        console.error('Failed to extract tokens from response');
        return null;
      }

      // Validate token structure
      if (!this.validateTokenStructure(newTokens)) {
        console.error('Invalid token structure received');
        return null;
      }

      // Store new tokens
      storage.setItem('tokens', JSON.stringify(newTokens));

      // Update user if present in response
      if (response.data.user) {
        storage.setItem('user', JSON.stringify(response.data.user));
      }

      console.info('✅ Token refresh successful');

      return newTokens;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      
      // If refresh token is invalid or expired, clear all auth data
      if (this.isAuthError(error)) {
        this.clearAuthData();
      }
      
      return null;
    }
  }

  /**
   * Extract tokens from various response structures
   * Adapter Pattern: Adapts different response formats to consistent token structure
   */
  private extractTokensFromResponse(data: any): AuthTokens | null {
    try {
      // Handle wrapped response: { data: { tokens } }
      if (data.data?.accessToken && data.data?.refreshToken) {
        return data.data as AuthTokens;
      }

      // Handle nested response: { data: { data: { tokens } } }
      if (data.data?.data?.accessToken && data.data?.data?.refreshToken) {
        return data.data.data as AuthTokens;
      }

      // Handle response with tokens key: { tokens: { ... } }
      if (data.tokens?.accessToken && data.tokens?.refreshToken) {
        return data.tokens as AuthTokens;
      }

      // Handle direct response: { accessToken, refreshToken }
      if (data.accessToken && data.refreshToken) {
        return data as AuthTokens;
      }

      return null;
    } catch (error) {
      console.error('Failed to extract tokens from response:', error);
      return null;
    }
  }

  /**
   * Validate token structure
   */
  private validateTokenStructure(tokens: any): boolean {
    return !!(
      tokens &&
      typeof tokens.accessToken === 'string' &&
      typeof tokens.refreshToken === 'string' &&
      typeof tokens.expiresIn === 'number' &&
      tokens.tokenType === 'Bearer'
    );
  }

  /**
   * Check if error is authentication-related
   */
  private isAuthError(error: any): boolean {
    if (error instanceof AxiosError) {
      return error.response?.status === 401 || error.response?.status === 403;
    }
    return false;
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    try {
      const keysToRemove = ['tokens', 'user', 'rememberMe', 'accessToken', 'refreshToken'];
      
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });

      console.info('🧹 Authentication data cleared');
    } catch (error) {
      console.warn('Failed to clear auth data:', error);
    }
  }

  /**
   * Dispatch token refresh events
   * Observer Pattern: Notifies subscribers of token state changes
   */
  private dispatchTokenRefreshEvent(
    status: 'success' | 'failure',
    tokens: AuthTokens | null
  ): void {
    const event = new CustomEvent('auth:token_refreshed', {
      detail: {
        status,
        tokens,
        timestamp: new Date().toISOString(),
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Reset coordinator state (useful for testing)
   */
  public reset(): void {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.failedRequestsQueue = [];
  }
}

/**
 * Response interceptor implementation with comprehensive features:
 * - Proactive and reactive token refresh
 * - Response validation and transformation
 * - Error normalization
 * - Performance monitoring
 * - Security validation
 * - Logging and analytics
 * 
 * Architecture:
 * - Strategy Pattern: Different error handling strategies
 * - Chain of Responsibility: Error handlers chain
 * - Template Method: Response processing flow
 */
export class ResponseInterceptorImpl {
  private performanceMetrics: Map<string, number> = new Map();
  private tokenRefreshCoordinator: TokenRefreshCoordinator;
  private axiosInstance: AxiosInstance | null = null;

  constructor() {
    this.tokenRefreshCoordinator = TokenRefreshCoordinator.getInstance();
  }

  /**
   * Set axios instance for token refresh requests
   */
  public setAxiosInstance(instance: AxiosInstance): void {
    this.axiosInstance = instance;
    this.tokenRefreshCoordinator.setAxiosInstance(instance);
  }

  /**
   * Handle successful responses
   * 
   * Template Method Pattern: Defines response processing flow
   */
  public onFulfilled = <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
    // Record performance metrics
    this.recordResponseMetrics(response);

    // Validate response security
    this.validateResponseSecurity(response);

    // Update authentication tokens if provided in response headers
    this.handleTokenRefreshFromHeaders(response);

    // Log successful response (in development)
    this.logResponse(response);

    return response;
  };

  /**
   * Handle response errors with comprehensive error handling
   * 
   * Strategy Pattern: Different error handling strategies based on error type
   */
  public onRejected = async (error: AxiosError): Promise<any> => {
    // Record error metrics
    this.recordErrorMetrics(error);

    const originalRequest = error.config as any;

    // Handle 401 Unauthorized with token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      return this.handleUnauthorizedWithRefresh(error, originalRequest);
    }

    // Handle specific error types using Chain of Responsibility pattern
    return this.handleError(error);
  };

  /**
   * Handle 401 Unauthorized errors with token refresh
   * 
   * Implements token refresh with proper queuing and error handling
   */
  private async handleUnauthorizedWithRefresh(
    error: AxiosError,
    originalRequest: any
  ): Promise<any> {
    // If already refreshing, queue this request
    if (this.tokenRefreshCoordinator.getIsRefreshing()) {
      try {
        await this.tokenRefreshCoordinator.enqueueRequest();
        // After refresh completes, retry with new token
        return this.retryRequestWithNewToken(originalRequest);
      } catch (queueError) {
        return Promise.reject(queueError);
      }
    }

    // Mark request as retried to prevent infinite loops
    originalRequest._retry = true;

    try {
      // Attempt to refresh the token
      const newTokens = await this.tokenRefreshCoordinator.refreshToken();

      if (newTokens) {
        // Update the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newTokens.accessToken}`;
        }

        // Retry the original request with new token
        return this.axiosInstance?.request(originalRequest);
      } else {
        // Token refresh failed
        throw new Error('Token refresh failed');
      }
    } catch (refreshError) {
      // Token refresh failed - handle logout
      const requestId = originalRequest.headers?.['X-Request-ID'] || 'unknown';
      const errorResponse = this.handleUnauthorizedError(error, requestId);
      return Promise.reject(errorResponse);
    }
  }

  /**
   * Retry request with new token after successful refresh
   */
  private async retryRequestWithNewToken(originalRequest: any): Promise<any> {
    try {
      // Get the new token from storage
      const tokensString = sessionStorage.getItem('tokens') || localStorage.getItem('tokens');
      
      if (tokensString) {
        const tokens = JSON.parse(tokensString);
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
        }
      }

      return this.axiosInstance?.request(originalRequest);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Record response performance metrics
   */
  private recordResponseMetrics(response: AxiosResponse): void {
    try {
      const requestStartTime = response.config.headers?.['X-Client-Start-Time'];
      if (requestStartTime) {
        const duration = Date.now() - parseInt(requestStartTime as string, 10);
        const requestId = response.config.headers?.['X-Request-ID'] as string;

        if (requestId) {
          this.performanceMetrics.set(requestId, duration);
        }

        // Log slow requests
        if (duration > 5000) {
          // 5 seconds
          console.warn(`⚠️ Slow request detected: ${response.config.url} took ${duration}ms`);
        }
      }
    } catch (error) {
      console.warn('Failed to record response metrics:', error);
    }
  }

  /**
   * Record error metrics
   */
  private recordErrorMetrics(error: AxiosError): void {
    try {
      const requestStartTime = error.config?.headers?.['X-Client-Start-Time'];
      if (requestStartTime) {
        const duration = Date.now() - parseInt(requestStartTime as string, 10);
        const requestId = error.config?.headers?.['X-Request-ID'] as string;

        if (requestId) {
          this.performanceMetrics.set(`${requestId}_error`, duration);
        }
      }
    } catch (err) {
      console.warn('Failed to record error metrics:', err);
    }
  }

  /**
   * Validate response security headers and content
   */
  private validateResponseSecurity(response: AxiosResponse): void {
    try {
      // Check for required security headers
      const securityHeaders = [
        'X-Content-Type-Options',
        'X-Frame-Options',
        'X-XSS-Protection',
        'Content-Security-Policy',
      ];

      const missingHeaders = securityHeaders.filter(
        (header) => !response.headers[header.toLowerCase()]
      );

      if (missingHeaders.length > 0 && import.meta.env.MODE === 'development') {
        console.warn('⚠️ Missing security headers:', missingHeaders);
      }

      // Validate content type
      const contentType = response.headers['content-type'];
      if (contentType && !contentType.includes('application/json') && response.data) {
        console.warn('⚠️ Unexpected content type:', contentType);
      }

      // Check for suspicious content
      if (typeof response.data === 'string' && this.containsSuspiciousContent(response.data)) {
        console.warn('⚠️ Response contains potentially suspicious content');
      }
    } catch (error) {
      console.warn('Failed to validate response security:', error);
    }
  }

  /**
   * Check for suspicious content in response
   */
  private containsSuspiciousContent(content: string): boolean {
    const suspiciousPatterns = [
      /<script[^>]*>/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe[^>]*>/i,
    ];

    return suspiciousPatterns.some((pattern) => pattern.test(content));
  }

  /**
   * Handle token refresh from response headers
   * 
   * Some APIs send refreshed tokens in response headers for security
   */
  private handleTokenRefreshFromHeaders(response: AxiosResponse): void {
    try {
      const newAccessToken = response.headers['x-new-access-token'];
      const newRefreshToken = response.headers['x-new-refresh-token'];

      if (newAccessToken || newRefreshToken) {
        // Get current tokens
        const storedTokens = sessionStorage.getItem('tokens') || localStorage.getItem('tokens');
        const isRememberMe = localStorage.getItem('rememberMe') === 'true';
        const storage = isRememberMe ? localStorage : sessionStorage;

        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);

          // Update tokens with new values
          const updatedTokens = {
            ...tokens,
            ...(newAccessToken && { accessToken: newAccessToken }),
            ...(newRefreshToken && { refreshToken: newRefreshToken }),
          };

          // Store updated tokens
          storage.setItem('tokens', JSON.stringify(updatedTokens));
          console.info('✅ Tokens refreshed from response headers');
        }
      }
    } catch (error) {
      console.warn('Failed to handle token refresh from headers:', error);
    }
  }

  /**
   * Log successful responses (development only)
   */
  private logResponse(response: AxiosResponse): void {
    if (import.meta.env.MODE === 'development') {
      const requestId = response.config.headers?.['X-Request-ID'];
      const duration = this.performanceMetrics.get(requestId as string);

      console.group(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`);
      console.log('Status:', response.status);
      console.log('Duration:', duration ? `${duration}ms` : 'unknown');
      console.log('Response:', response.data);
      console.groupEnd();
    }
  }

  /**
   * Handle errors using Chain of Responsibility pattern
   */
  private handleError(error: AxiosError): Promise<ApiErrorResponse> {
    if (error.response) {
      // Server responded with error status
      return Promise.reject(this.handleServerError(error));
    } else if (error.request) {
      // Request was made but no response received
      return Promise.reject(this.handleNetworkError(error));
    } else {
      // Something else happened
      return Promise.reject(this.handleGenericError(error));
    }
  }

  /**
   * Handle server errors (4xx, 5xx)
   * 
   * Strategy Pattern: Different handling for different status codes
   */
  private handleServerError(error: AxiosError): ApiErrorResponse {
    const response = error.response!;
    const requestId = (error.config?.headers?.['X-Request-ID'] as string) || 'unknown';

    // Map status codes to handlers
    const errorHandlers: Record<number, () => ApiErrorResponse> = {
      401: () => this.handleUnauthorizedError(error, requestId),
      403: () => this.handleForbiddenError(error, requestId),
      404: () => this.handleNotFoundError(error, requestId),
      422: () => this.handleValidationError(error, requestId),
      429: () => this.handleRateLimitError(error, requestId),
      500: () => this.handleServerInternalError(error, requestId),
      502: () => this.handleServerInternalError(error, requestId),
      503: () => this.handleServerInternalError(error, requestId),
      504: () => this.handleServerInternalError(error, requestId),
    };

    const handler = errorHandlers[response.status];
    return handler ? handler() : this.createErrorResponse(error, requestId);
  }

  /**
   * Handle 401 Unauthorized errors (called only after token refresh fails)
   */
  private handleUnauthorizedError(error: AxiosError, requestId: string): ApiErrorResponse {
    console.warn('🚫 Authentication failed - session expired');

    // Clear authentication tokens
    this.clearAuthTokens();

    // Dispatch logout event
    window.dispatchEvent(new CustomEvent('auth:logout', { detail: 'unauthorized' }));

    return {
      success: false,
      message: 'Your session has expired. Please log in again.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'UNAUTHORIZED',
        type: 'AUTHENTICATION',
        details: error.response?.data as Record<string, unknown>,
      },
    };
  }

  /**
   * Handle 403 Forbidden errors
   */
  private handleForbiddenError(error: AxiosError, requestId: string): ApiErrorResponse {
    return {
      success: false,
      message: 'You do not have permission to perform this action.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'FORBIDDEN',
        type: 'AUTHORIZATION',
        details: error.response?.data as Record<string, unknown>,
      },
    };
  }

  /**
   * Handle 404 Not Found errors
   */
  private handleNotFoundError(error: AxiosError, requestId: string): ApiErrorResponse {
    return {
      success: false,
      message: 'The requested resource was not found.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'NOT_FOUND',
        type: 'NOT_FOUND',
        details: error.response?.data as Record<string, unknown>,
      },
    };
  }

  /**
   * Handle 422 Validation errors
   */
  private handleValidationError(error: AxiosError, requestId: string): ApiErrorResponse {
    const responseData = error.response?.data as any;

    return {
      success: false,
      message: 'Validation failed. Please check your input.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'VALIDATION_ERROR',
        type: 'VALIDATION',
        details: responseData?.errors || responseData,
      },
    };
  }

  /**
   * Handle 429 Rate Limit errors
   */
  private handleRateLimitError(error: AxiosError, requestId: string): ApiErrorResponse {
    const retryAfter = error.response?.headers['retry-after'];

    return {
      success: false,
      message: 'Too many requests. Please try again later.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'RATE_LIMIT_EXCEEDED',
        type: 'SERVER_ERROR',
        details: {
          retryAfter: retryAfter ? parseInt(retryAfter, 10) : undefined,
        },
      },
    };
  }

  /**
   * Handle 5xx server internal errors
   */
  private handleServerInternalError(error: AxiosError, requestId: string): ApiErrorResponse {
    return {
      success: false,
      message: 'A server error occurred. Please try again later.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: `SERVER_ERROR_${error.response?.status}`,
        type: 'SERVER_ERROR',
        details: error.response?.data as Record<string, unknown>,
      },
    };
  }

  /**
   * Handle network errors (no response received)
   */
  private handleNetworkError(error: AxiosError): ApiErrorResponse {
    const requestId = (error.config?.headers?.['X-Request-ID'] as string) || 'unknown';

    return {
      success: false,
      message: 'Network error. Please check your internet connection.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'NETWORK_ERROR',
        type: 'NETWORK_ERROR',
        details: {
          message: error.message,
          code: error.code,
        },
      },
    };
  }

  /**
   * Handle generic errors
   */
  private handleGenericError(error: AxiosError): ApiErrorResponse {
    const requestId = (error.config?.headers?.['X-Request-ID'] as string) || 'unknown';

    return {
      success: false,
      message: 'An unexpected error occurred.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: 'UNKNOWN_ERROR',
        type: 'SERVER_ERROR',
        details: {
          message: error.message,
        },
      },
    };
  }

  /**
   * Create generic error response
   */
  private createErrorResponse(error: AxiosError, requestId: string): ApiErrorResponse {
    const responseData = error.response?.data as any;

    return {
      success: false,
      message: responseData?.message || 'An error occurred while processing your request.',
      timestamp: new Date().toISOString(),
      requestId,
      error: {
        code: responseData?.error?.code || `HTTP_${error.response?.status}`,
        type: 'SERVER_ERROR',
        details: responseData,
      },
    };
  }

  /**
   * Clear authentication tokens
   */
  private clearAuthTokens(): void {
    try {
      const keysToRemove = [
        'accessToken',
        'refreshToken',
        'tokens',
        'user',
        'rememberMe',
      ];

      keysToRemove.forEach((key) => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('Failed to clear auth tokens:', error);
    }
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): Map<string, number> {
    return new Map(this.performanceMetrics);
  }

  /**
   * Clear performance metrics
   */
  public clearPerformanceMetrics(): void {
    this.performanceMetrics.clear();
  }

  /**
   * Get average response time for successful requests
   */
  public getAverageResponseTime(): number {
    const successfulRequests = Array.from(this.performanceMetrics.entries())
      .filter(([key]) => !key.includes('_error'))
      .map(([, duration]) => duration);

    if (successfulRequests.length === 0) return 0;

    const total = successfulRequests.reduce((sum, duration) => sum + duration, 0);
    return total / successfulRequests.length;
  }

  /**
   * Get error rate percentage
   */
  public getErrorRate(): number {
    const totalRequests = this.performanceMetrics.size;
    if (totalRequests === 0) return 0;

    const errorRequests = Array.from(this.performanceMetrics.keys()).filter((key) =>
      key.includes('_error')
    ).length;

    return (errorRequests / totalRequests) * 100;
  }

  /**
   * Get token refresh coordinator instance (for testing/debugging)
   */
  public getTokenRefreshCoordinator(): TokenRefreshCoordinator {
    return this.tokenRefreshCoordinator;
  }
}
